import { motion } from "framer-motion";
import { Users, BadgeCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { imgSrc, focusStyle } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

export const PeopleCards = ({ items, testidPrefix = "member", onSelect }) => {
  const { t, lang } = useSite();
  return (
    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((m, i) => (
        <motion.div
          key={m.id || i}
          data-testid={`${testidPrefix}-card-${i}`}
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
          onClick={onSelect ? () => onSelect(m) : undefined}
          className={`group relative rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-2 hover:shadow-[0_24px_48px_rgb(56,189,248,0.18)] transition-[transform,box-shadow] duration-400 ${onSelect ? "cursor-pointer" : ""}`}
        >
          <div className="relative aspect-[4/3] overflow-hidden bg-sky-100">
            {imgSrc(m.avatar) ? (
              <img
                src={imgSrc(m.avatar)}
                alt={t(m, "name")}
                loading="lazy"
                style={focusStyle(m)}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center font-display font-black text-5xl text-sky-300">
                {(t(m, "name") || "?")[0]}
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
          </div>
          <div className="p-6 pt-2">
            <h3 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2">
              {t(m, "name")}
            </h3>
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-100 px-3 py-1 text-xs font-bold text-orange-600">
              <BadgeCheck size={13} />
              {t(m, "role")}
            </p>
            {onSelect && (
              <p className="mt-3 text-xs font-bold text-sky-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {lang === "zh" ? "点击查看详细资料 →" : "Click for full profile →"}
              </p>
            )}
            {(m.join_url || m.policy_url) && (
              <div className="mt-5 flex gap-2.5">
                {m.join_url && (
                  <a
                    data-testid={`${testidPrefix}-join-${i}`}
                    href={m.join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center rounded-full bg-orange-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-orange-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
                  >
                    {lang === "zh" ? "我想加入" : "Join Us"}
                  </a>
                )}
                {m.policy_url && (
                  <a
                    data-testid={`${testidPrefix}-policy-${i}`}
                    href={m.policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center rounded-full border-2 border-sky-400 px-4 py-2 text-xs font-bold text-sky-500 hover:bg-sky-400 hover:text-white hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300"
                  >
                    {lang === "zh" ? "了解保单" : "View Plans"}
                  </a>
                )}
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export const Members = ({ onSelect }) => {
  const { content, lang } = useSite();
  const members = content.members || [];
  if (members.length === 0) return null;

  return (
    <section id="members" data-testid="members-section" className="py-32 bg-[#F8FAFC] relative overflow-hidden">
      <div className="absolute top-20 -left-24 w-80 h-80 rounded-full bg-orange-200/40 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">
              {lang === "zh" ? "团队成员" : "Our People"}
            </p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900">
              {lang === "zh" ? "我们的伙伴" : "Our Partners"}
            </h2>
          </div>
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-500">
            <Users size={16} className="text-sky-500" />
            {members.length} {lang === "zh" ? "位核心成员" : "core members"}
          </p>
        </motion.div>
        <PeopleCards items={members} testidPrefix="member" onSelect={onSelect} />
      </div>
    </section>
  );
};
